import type { Readable } from "node:stream";

import { CliError } from "../errors/index.js";
import type { BoardMetadata } from "../model/board.js";
import type { BoardProfile, JsonObject } from "../model/common.js";
import type { PositionRecord, PositionMoveKind, BoardState } from "../model/position.js";
import type { ExecutionContext } from "../runtime/context.js";
import { resolveContentSpec } from "../runtime/content.js";
import { evaluateBoardState } from "./evaluate.js";
import { computeStateHash } from "./hash.js";
import { recordProof, selectLine, upsertAtom, upsertCell } from "./mutations.js";
import {
  createBoardMetadataClaim,
  finalizeBoardMetadata,
  requireReadyBoardMetadata,
  updateBoardMetadata,
} from "../repo/board-meta.js";
import {
  allocateRecordId,
  initializeBoardStore,
  insertPosition,
  listPositions,
  loadPosition,
  withBoardDatabase,
  withWriteTransaction,
} from "../repo/board-store.js";

type CommitHelpers = {
  allocateAtomId(requestedId: string | undefined): string;
  allocateCellId(requestedId: string | undefined): string;
};

type MutationResult<T> = T & {
  movePayload: JsonObject;
  moveSummary: string;
};

export async function createBoard(
  context: ExecutionContext,
  sessionKey: string,
  profile: BoardProfile,
) {
  await createBoardMetadataClaim(context, sessionKey, profile);
  const rootPosition = buildRootPosition();

  await withBoardDatabase(context, sessionKey, (database) => {
    initializeBoardStore(database, rootPosition);
  });

  const board = await finalizeBoardMetadata(context, sessionKey, {
    rootPositionId: rootPosition.id,
    tipPositionId: rootPosition.id,
  });

  return {
    board,
    rootPosition,
  };
}

export async function loadBoardForCommand(
  context: ExecutionContext,
  sessionKey: string,
) {
  return await requireReadyBoardMetadata(context, sessionKey);
}

export async function loadPositionForCommand(
  context: ExecutionContext,
  sessionKey: string,
  ref: string | undefined,
) {
  const board = await loadBoardForCommand(context, sessionKey);
  const positionId = resolvePositionRef(board, ref);
  const position = await withBoardDatabase(context, sessionKey, (database) =>
    loadPosition(database, positionId),
  );
  if (position === undefined) {
    throw new CliError("POSITION_NOT_FOUND", `Position "${positionId}" was not found`);
  }
  return { board, position };
}

export async function listBoardPositions(
  context: ExecutionContext,
  sessionKey: string,
) {
  await loadBoardForCommand(context, sessionKey);
  return await withBoardDatabase(context, sessionKey, (database) => listPositions(database));
}

export async function commitAtomUpsert(
  context: ExecutionContext,
  sessionKey: string,
  input: {
    atomId: string | undefined;
    bodySpec: string | undefined;
    fromPositionRef: string | undefined;
    kind: Parameters<typeof upsertAtom>[2]["kind"];
    status: Parameters<typeof upsertAtom>[2]["status"];
    stdin: Readable;
    tags: string[];
    title: string | undefined;
  },
) {
  const body = await resolveContentSpec(context.cwd, input.bodySpec, input.stdin, {
    kind: "atom body",
    required: true,
  });
  if (body === undefined) {
    throw new CliError("EMPTY_INPUT", "atom body expects content");
  }
  return await commitMutation(context, sessionKey, input.fromPositionRef, "atom.upsert", (state, helpers) =>
    upsertAtom(state, helpers, {
      atomId: input.atomId,
      body,
      kind: input.kind,
      status: input.status,
      tags: input.tags,
      title: input.title,
    }),
  );
}

export async function commitCellUpsert(
  context: ExecutionContext,
  sessionKey: string,
  input: {
    cellId: string | undefined;
    effect: Parameters<typeof upsertCell>[2]["effect"];
    from: string;
    fromPositionRef: string | undefined;
    to: string;
  },
) {
  return await commitMutation(context, sessionKey, input.fromPositionRef, "cell.upsert", (state, helpers) =>
    upsertCell(state, helpers, input),
  );
}

export async function commitLineSelect(
  context: ExecutionContext,
  sessionKey: string,
  input: {
    fromPositionRef: string | undefined;
    pathId: string;
  },
) {
  return await commitMutation(context, sessionKey, input.fromPositionRef, "line.select", (state) =>
    selectLine(state, { pathId: input.pathId }),
  );
}

export async function commitProofRecord(
  context: ExecutionContext,
  sessionKey: string,
  input: {
    bodySpec: string | undefined;
    fromPositionRef: string | undefined;
    proofId: string | undefined;
    stdin: Readable;
    targetAtomId: string;
    verdict: "fail" | "pass";
  },
) {
  const body = await resolveContentSpec(context.cwd, input.bodySpec, input.stdin, {
    kind: "proof body",
    required: true,
  });
  if (body === undefined) {
    throw new CliError("EMPTY_INPUT", "proof body expects content");
  }
  return await commitMutation(context, sessionKey, input.fromPositionRef, "proof.record", (state, helpers) =>
    recordProof(state, helpers, {
      body,
      proofId: input.proofId,
      targetAtomId: input.targetAtomId,
      verdict: input.verdict,
    }),
  );
}

export function resolvePositionRef(board: BoardMetadata, ref: string | undefined) {
  if (ref === undefined || ref === "@tip") {
    return board.tipPositionId;
  }
  if (ref === "@root") {
    return board.rootPositionId;
  }
  return ref;
}

async function commitMutation<T extends MutationResult<Record<string, unknown>>>(
  context: ExecutionContext,
  sessionKey: string,
  fromPositionRef: string | undefined,
  moveKind: PositionMoveKind,
  mutate: (state: BoardState, helpers: CommitHelpers) => T,
) {
  const board = await loadBoardForCommand(context, sessionKey);
  const parentPositionId = resolvePositionRef(board, fromPositionRef);

  const result = await withBoardDatabase(context, sessionKey, (database) =>
    withWriteTransaction(database, () => {
      const parent = loadPosition(database, parentPositionId);
      if (parent === undefined) {
        throw new CliError("POSITION_NOT_FOUND", `Position "${parentPositionId}" was not found`);
      }

      const state = structuredClone(parent.stateSnapshot);
      const helpers: CommitHelpers = {
        allocateAtomId: (requestedId) => allocateRecordId(database, "atom", "a", requestedId),
        allocateCellId: (requestedId) => allocateRecordId(database, "cell", "c", requestedId),
      };
      const mutation = mutate(state, helpers);
      const stateHash = computeStateHash(state);
      if (stateHash === parent.stateHash) {
        return {
          mutation,
          parent,
          position: parent,
        };
      }
      const detail = evaluateBoardState(state);
      const position: PositionRecord = {
        createdAt: new Date().toISOString(),
        depth: parent.depth + 1,
        evaluation: detail.evaluation,
        id: allocateRecordId(database, "position", "p", undefined),
        moveKind,
        movePayload: mutation.movePayload,
        moveSummary: mutation.moveSummary,
        parentPositionId: parent.id,
        stateHash,
        stateSnapshot: state,
      };
      insertPosition(database, position);
      return {
        mutation,
        parent,
        position,
      };
    }),
  );

  const nextBoard = await updateBoardMetadata(context, sessionKey, (metadata) => ({
    ...metadata,
    selectedPositionId: result.position.id,
    tipPositionId: result.position.id,
  }));

  return {
    board: nextBoard,
    parent: result.parent,
    position: result.position,
    ...result.mutation,
  };
}

function buildRootPosition(): PositionRecord {
  const state: BoardState = {
    atoms: {},
    cells: {},
    line: {},
  };
  const detail = evaluateBoardState(state);
  return {
    createdAt: new Date().toISOString(),
    depth: 0,
    evaluation: detail.evaluation,
    id: "p0",
    moveKind: "root",
    moveSummary: "Create empty board",
    parentPositionId: undefined,
    stateHash: computeStateHash(state),
    stateSnapshot: state,
  };
}
